import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { CreditCard } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

// FIN-3 — Donut de gastos por cartão no mês corrente.
// Consome props.spendingByCard direto do hook (já fetchado).

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL",
});

function _monthLabel(monthIso) {
  if (!monthIso) return "—";
  const [y, m] = monthIso.split("-");
  const labels = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${labels[parseInt(m, 10) - 1]} ${y}`;
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-surface-overlay border border-subtle rounded-lg px-3 py-2 shadow-2xl">
      <p className="text-caption font-semibold text-foreground mb-0.5">{d.name}</p>
      <p className="text-micro text-foreground-muted tabular-nums">
        {fmtBRL(d.total)} ({d.pct}%)
      </p>
    </div>
  );
}

export default function GastosPorCartaoDonut({ spendingByCard }) {
  const total = Number(spendingByCard?.total ?? 0);
  const cards = (spendingByCard?.by_card || []).filter(c => c.total > 0);
  const monthLabel = _monthLabel(spendingByCard?.month);

  return (
    <Card variant="elevated" className="glass-card glow-hover p-5 md:p-6 min-h-[280px] flex flex-col" data-testid="contas-donut-chart">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-base md:text-lg font-bold text-foreground">
          Gastos por cartão
        </h3>
        <Badge variant="info" className="text-micro capitalize">
          {monthLabel}
        </Badge>
      </div>

      {cards.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-foreground-muted">
          <CreditCard className="w-8 h-8 opacity-40" aria-hidden="true" />
          <p className="text-caption">Sem gastos neste mês</p>
        </div>
      ) : (
        <div className="flex flex-col md:flex-row items-center gap-4 flex-1 min-h-[220px]">
          <div className="w-full md:w-[55%] h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={cards}
                  dataKey="total"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={48}
                  outerRadius={78}
                  paddingAngle={2}
                  stroke="rgba(0,0,0,0.4)"
                  strokeWidth={2}
                >
                  {cards.map((c) => (
                    <Cell key={c.account_id} fill={c.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="flex-1 w-full md:w-auto space-y-1.5 max-h-[200px] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-subtle/40 pb-1.5 mb-1.5">
              <span className="text-micro text-foreground-muted uppercase font-semibold" style={{ letterSpacing: "0.08em" }}>
                Total
              </span>
              <span className="font-mono font-semibold text-foreground tabular-nums">
                {fmtBRL(total)}
              </span>
            </div>
            {cards.map(c => (
              <div key={c.account_id} className="flex items-center gap-2 text-caption">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: c.color }}
                  aria-hidden="true"
                />
                <span className="flex-1 truncate text-foreground">{c.name}</span>
                <span className="text-foreground-muted tabular-nums">{c.pct.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%</span>
                <span className="font-mono font-medium text-foreground tabular-nums w-20 text-right">
                  {fmtBRL(c.total)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
